import React from 'react';
import { differenceInCalendarDays, parseISO, format, isValid } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { getStoredSubscriptionForCourse } from '../logic/authStorage';
import { getActiveCourseId } from '../logic/courseContext';

// Show the "Subscribe Now" CTA only this close to the end, so the first screens after
// sign-up don't ask for payment before any practice (STUD-896).
const SUBSCRIBE_CTA_DAYS = 3;

/**
 * Expiry and the displayed count are separate questions. Expired means the exact end
 * instant has passed. The count is in calendar days, so day 1 of a 7-day trial reads
 * 7 (differenceInDays floored it to 6) and the last day reads 0 ("ends today") until
 * the end time itself.
 */
export function trialDaysRemaining(endDate, now) {
    return {
        isExpired: endDate.getTime() <= now.getTime(),
        days: differenceInCalendarDays(endDate, now),
    };
}

/**
 * Persistent full-width banner shown to users on an active free trial.
 * Displays days remaining, and a "Subscribe Now" CTA linking to /checkout in the
 * last few days. Only renders when subscription status is 'trialing'.
 */
function TrialBanner({ isLoggedIn }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    if (!isLoggedIn) return null;

    // Only parents manage subscriptions — students and teachers should never see this
    const role = localStorage.getItem('studyapp_role');
    if (role !== 'parent') return null;

    // User is actively in the checkout or success flow — banner is contradictory here
    if (pathname === '/checkout' || pathname === '/checkout-success') return null;

    const activeCourseId = getActiveCourseId();
    const sub = getStoredSubscriptionForCourse(activeCourseId);

    // Parent already subscribed during trial — persisted in subscription entry or legacy flat key
    const paymentCommitted = sub?.paymentCommitted === true ||
        localStorage.getItem('studyapp_subscription_paymentCommitted') === 'true';
    // Session-level flag set immediately after SuccessPage confirms checkout
    const sessionCommitted = localStorage.getItem('studyapp_subscription_committed') === 'true';
    if (paymentCommitted || sessionCommitted) return null;

    const status = sub?.status;
    if (status !== 'trialing') return null;

    const endDateRaw = sub?.endDate;
    if (!endDateRaw) return null;

    const endDate = parseISO(endDateRaw);
    if (!isValid(endDate)) return null;

    const { isExpired, days: daysRemaining } = trialDaysRemaining(endDate, new Date());
    const showSubscribeCta = isExpired || daysRemaining <= SUBSCRIBE_CTA_DAYS;
    const formattedDate = format(endDate, 'MMM d, yyyy');

    // Pre-select the plan the parent originally signed up with
    const planKey = sub?.planKey;
    const checkoutParams = new URLSearchParams();
    if (planKey) checkoutParams.set('plan', planKey);
    if (activeCourseId) checkoutParams.set('courseId', activeCourseId);
    const checkoutPath = checkoutParams.toString()
        ? `/checkout?${checkoutParams.toString()}`
        : '/checkout';

    // Urgency colour: red if expired or last day, amber in last 3 days, blue otherwise
    let bannerStyle;
    if (isExpired || daysRemaining <= 0) {
        bannerStyle = {
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
        };
    } else if (daysRemaining <= 3) {
        bannerStyle = {
            backgroundColor: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffc107',
        };
    } else {
        bannerStyle = {
            backgroundColor: '#d1ecf1',
            color: '#0c5460',
            border: '1px solid #bee5eb',
        };
    }

    const daysLabel =
        isExpired
            ? 'Your free trial has expired'
            : daysRemaining === 0
            ? 'Your free trial ends today'
            : daysRemaining === 1
            ? 'Your free trial ends tomorrow'
            : `Your free trial ends in ${daysRemaining} days`;

    return (
        <div
            className="w-100 d-flex align-items-center justify-content-center flex-wrap gap-2"
            role="status"
            aria-live="polite"
            style={{
                ...bannerStyle,
                borderRadius: 2,
                padding: '4px 12px',
                fontWeight: 600,
                fontSize: '0.82rem',
                lineHeight: 1.3,
                marginTop: 2,
                marginBottom: 0,
                gap: '8px',
            }}
        >
            <span>
                ⏳ <strong>{daysLabel}</strong> — on {formattedDate}.{showSubscribeCta ? ' Subscribe now to keep full access.' : ''}
            </span>
            {showSubscribeCta && (
                <button
                    type="button"
                    onClick={() => navigate(checkoutPath)}
                    style={{
                        backgroundColor: daysRemaining <= 3 ? '#d9534f' : '#0066cc',
                        color: 'var(--text-inverse)',
                        border: 'none',
                        borderRadius: 4,
                        padding: '2px 12px',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Subscribe Now →
                </button>
            )}
        </div>
    );
}

export default TrialBanner;
